import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useForm, Controller } from 'react-hook-form'
import axios from 'axios'
import normalize from 'json-api-normalizer'
import Jsona from 'jsona'
import { Copy, Check } from 'lucide-react'

import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import { Checkbox } from '../../ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select'
import { DatePicker } from '../../ui/date-picker'

const jsonApiDataFormatter = new Jsona()

function extractCurrentSkillLevelId(student) {
  if (!student) return ''
  if (student.currentSkillLevel?.id != null) return String(student.currentSkillLevel.id)
  if (student.currentSkillLevelId != null) return String(student.currentSkillLevelId)
  if (student.currentSkillLevel?.data?.id != null) return String(student.currentSkillLevel.data.id)
  if (student.currentSkillLevel?.data != null) return String(student.currentSkillLevel.data)
  if (student.currentSkillLevel != null && typeof student.currentSkillLevel !== 'object') {
    return String(student.currentSkillLevel)
  }
  return ''
}

function extractCurrentSkillLevelName(student) {
  if (!student) return ''
  if (student.currentSkillLevel?.name) return student.currentSkillLevel.name
  if (student.currentSkillLevelName) return student.currentSkillLevelName
  return ''
}

function StudentEditForm({
  student,
  facilityId,
  onSuccess,
  onError,
  onSubmitExposed,
  onSubmittingChange,
  allowLevelEditing = true
}) {
  const [error, setError] = useState(null)
  const [skillLevels, setSkillLevels] = useState([])
  const [copiedField, setCopiedField] = useState(null)
  const selectedSkillLevelId = extractCurrentSkillLevelId(student)
  const selectedSkillLevelName = extractCurrentSkillLevelName(student)

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      fullName: '',
      preferredName: '',
      email: '',
      phone: '',
      birthdate: '',
      gender: 'unspecified',
      ltsNumber: '',
      skillLevel: '',
      addressLine1: '',
      addressLine2: '',
      addressCity: '',
      addressState: '',
      addressPostCode: '',
      skateRentalActive: false,
      skateRentalType: '',
      skateRentalSize: ''
    }
  })

  const skateRentalActive = watch('skateRentalActive')

  useEffect(() => {
    if (onSubmitExposed) {
      onSubmitExposed(handleSubmit(onSubmit))
    }
  }, [onSubmitExposed, handleSubmit])

  useEffect(() => {
    if (facilityId && allowLevelEditing) {
      axios
        .get(`/api/facilities/${facilityId}/skill_levels`)
        .then((response) => {
          const levels = jsonApiDataFormatter.denormalizeReduxObject({
            reduxObject: normalize(response.data),
            entityType: 'skillLevel'
          })

          if (levels && Array.isArray(levels)) {
            setSkillLevels(levels.map((level) => ({ ...level, id: String(level.id) })))
          }
        })
        .catch((err) => {
          console.error('Failed to load skill levels:', err)
        })
    }
  }, [facilityId])

  useEffect(() => {
    if (student) {
      const address = student.address || {}
      const skateRental = student.skateRental || {}

      reset({
        fullName: student.fullName || '',
        preferredName: student.preferredName || '',
        email: student.email || '',
        phone: student.phone || '',
        birthdate: student.birthdate || '',
        gender: student.gender || 'unspecified',
        ltsNumber: student.ltsNumber || '',
        skillLevel: extractCurrentSkillLevelId(student),
        addressLine1: address.line1 || '',
        addressLine2: address.line2 || '',
        addressCity: address.city || '',
        addressState: address.state || '',
        addressPostCode: address.postCode || '',
        skateRentalActive: skateRental.active || false,
        skateRentalType: skateRental.skateType || '',
        skateRentalSize: skateRental.size || ''
      })
    }
  }, [student, reset])

  const skillLevelOptions = (() => {
    if (!selectedSkillLevelId) return skillLevels
    const hasSelectedLevel = skillLevels.some((level) => String(level.id) === selectedSkillLevelId)
    if (hasSelectedLevel) return skillLevels
    return [{ id: selectedSkillLevelId, name: selectedSkillLevelName || 'Current level' }, ...skillLevels]
  })()

  async function onSubmit(data) {
    if (onSubmittingChange) onSubmittingChange(true)
    setError(null)

    try {
      let formattedBirthdate = data.birthdate
      if (data.birthdate instanceof Date) {
        const year = data.birthdate.getFullYear()
        const month = String(data.birthdate.getMonth() + 1).padStart(2, '0')
        const day = String(data.birthdate.getDate()).padStart(2, '0')
        formattedBirthdate = `${year}-${month}-${day}`
      }

      const normalizeEmail = (value) => value?.trim().toLowerCase() || ''
      const originalStudentEmail = normalizeEmail(student.email)
      const submittedEmail = normalizeEmail(data.email)

      const studentAttributes = {
        full_name: data.fullName,
        preferred_name: data.preferredName,
        birthdate: formattedBirthdate || null,
        gender: data.gender,
        lts_number: data.ltsNumber || null
      }

      if (allowLevelEditing) {
        studentAttributes.current_skill_level_id = data.skillLevel && data.skillLevel !== '-1' ? data.skillLevel : null
      }

      if (submittedEmail !== originalStudentEmail) {
        studentAttributes.email = submittedEmail || null
      }

      const submittedPhone = data.phone?.trim() || ''
      const originalPhone = student.phone?.trim() || ''
      if (submittedPhone !== originalPhone) {
        studentAttributes.phone = submittedPhone || null
      }

      const hasAddressData = data.addressLine1 || data.addressCity
      const hasSkateRentalData = data.skateRentalActive || student.skateRental
      const userAttributes = {}

      if (hasAddressData) {
        userAttributes.address = {
          line1: data.addressLine1 || '',
          line2: data.addressLine2 || '',
          city: data.addressCity || '',
          state: data.addressState || '',
          post_code: data.addressPostCode || ''
        }
      }

      if (hasSkateRentalData) {
        userAttributes.skate_rental_attributes = {
          active: data.skateRentalActive,
          skate_type: data.skateRentalType || null,
          size: data.skateRentalSize ? parseFloat(data.skateRentalSize) : null
        }
      }

      let response
      if (allowLevelEditing) {
        response = await axios.patch(`/api/facilities/${facilityId}/students/${student.id}`, {
          student: studentAttributes
        })
        if (hasAddressData || hasSkateRentalData) {
          await axios.patch(`/api/facilities/${facilityId}/users/${student.id}`, { user: userAttributes })
        }
      } else {
        // Parent profile updates preserve facility memberships and leave staff-managed levels unchanged.
        response = await axios.patch(`/api/users/${student.id}`, {
          user: { ...studentAttributes, ...userAttributes }
        })
      }

      if (onSuccess) {
        onSuccess(response.data)
      }
    } catch (err) {
      const errorMessage =
        (err.response && err.response.data && err.response.data.errors && err.response.data.errors.join(', ')) ||
        'An error occurred while updating the student'
      setError(errorMessage)
      if (onError) onError(errorMessage)
    } finally {
      if (onSubmittingChange) onSubmittingChange(false)
    }
  }

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'non_binary', label: 'Non-Binary' },
    { value: 'unspecified', label: 'Prefer not to say' }
  ]

  const skateTypeOptions = [
    { value: 'youth_figure', label: 'Youth Figure' },
    { value: 'men_figure', label: "Men's Figure" },
    { value: 'women_figure', label: "Women's Figure" },
    { value: 'men_hockey', label: "Men's Hockey" },
    { value: 'women_hockey', label: "Women's Hockey" },
    { value: 'youth_hockey', label: 'Youth Hockey' }
  ]

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" id="student-edit-form">
      {error && (
        <div className="p-4 rounded-md bg-red-50 border border-red-200">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="fullName">Full Name</Label>
        <Controller
          name="fullName"
          control={control}
          rules={{ required: 'Full name is required' }}
          render={({ field }) => (
            <Input id="fullName" type="text" {...field} className={errors.fullName ? 'border-red-500' : ''} />
          )}
        />
        {errors.fullName && <p className="text-sm text-red-600">{errors.fullName.message}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="preferredName">Preferred Name</Label>
        <Controller
          name="preferredName"
          control={control}
          render={({ field }) => (
            <Input id="preferredName" type="text" {...field} className={errors.preferredName ? 'border-red-500' : ''} />
          )}
        />
        {errors.preferredName && <p className="text-sm text-red-600">{errors.preferredName.message}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Controller
          name="email"
          control={control}
          rules={{
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Invalid email address'
            }
          }}
          render={({ field }) => (
            <div className="relative">
              <Input id="email" type="email" {...field} className={`${errors.email ? 'border-red-500' : ''} pr-10`} />
              {field.value && (
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(field.value)
                    setCopiedField('email')
                    setTimeout(() => setCopiedField(null), 2000)
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-200 transition-colors"
                >
                  {copiedField === 'email' ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4 text-gray-400" />
                  )}
                </button>
              )}
            </div>
          )}
        />
        {errors.email && <p className="text-sm text-red-600">{errors.email.message}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone">Phone</Label>
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <div className="relative">
              <Input id="phone" type="tel" {...field} className="pr-10" />
              {field.value && (
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(field.value)
                    setCopiedField('phone')
                    setTimeout(() => setCopiedField(null), 2000)
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-200 transition-colors"
                >
                  {copiedField === 'phone' ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4 text-gray-400" />
                  )}
                </button>
              )}
            </div>
          )}
        />
      </div>

      {student.guardians?.length > 0 && (
        <>
          <div className="space-y-2">
            <Label>Guardian Email</Label>
            <div className="relative">
              <Input
                type="email"
                value={student.guardians[0].email || ''}
                disabled
                className="bg-gray-50 text-gray-600 pr-10"
              />
              {student.guardians[0].email && (
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(student.guardians[0].email)
                    setCopiedField('guardianEmail')
                    setTimeout(() => setCopiedField(null), 2000)
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-200 transition-colors"
                >
                  {copiedField === 'guardianEmail' ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4 text-gray-400" />
                  )}
                </button>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Guardian Phone</Label>
            <div className="relative">
              <Input
                type="tel"
                value={student.guardians[0].phone || ''}
                disabled
                className="bg-gray-50 text-gray-600 pr-10"
              />
              {student.guardians[0].phone && (
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(student.guardians[0].phone)
                    setCopiedField('guardianPhone')
                    setTimeout(() => setCopiedField(null), 2000)
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-200 transition-colors"
                >
                  {copiedField === 'guardianPhone' ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4 text-gray-400" />
                  )}
                </button>
              )}
            </div>
          </div>
        </>
      )}

      <div className="space-y-2">
        <Label htmlFor="birthdate">Birthdate</Label>
        <Controller
          name="birthdate"
          control={control}
          render={({ field }) => (
            <DatePicker value={field.value} onChange={field.onChange} placeholder="Select birthdate" />
          )}
        />
        {errors.birthdate && <p className="text-sm text-red-600">{errors.birthdate.message}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="gender">Gender</Label>
        <Controller
          name="gender"
          control={control}
          render={({ field }) => (
            <Select onValueChange={field.onChange} value={field.value}>
              <SelectTrigger className={errors.gender ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select gender" />
              </SelectTrigger>
              <SelectContent>
                {genderOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {errors.gender && <p className="text-sm text-red-600">{errors.gender.message}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="ltsNumber">LTS Number</Label>
        <Controller
          name="ltsNumber"
          control={control}
          render={({ field }) => (
            <Input
              id="ltsNumber"
              type="text"
              {...field}
              placeholder="LTS or USFSA number"
              className={errors.ltsNumber ? 'border-red-500' : ''}
            />
          )}
        />
        {errors.ltsNumber && <p className="text-sm text-red-600">{errors.ltsNumber.message}</p>}
      </div>

      {allowLevelEditing && (
        <div className="space-y-2">
          <Label htmlFor="skillLevel">Skill Level</Label>
          <Controller
            name="skillLevel"
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className={errors.skillLevel ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Select skill level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="-1">I don&apos;t know</SelectItem>
                  {skillLevelOptions.map((level) => (
                    <SelectItem key={level.id} value={String(level.id)}>
                      {level.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.skillLevel && <p className="text-sm text-red-600">{errors.skillLevel.message}</p>}
        </div>
      )}

      <div className="pt-4 border-t border-gray-200">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Address</h4>

        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="addressLine1">Address Line 1</Label>
            <Controller
              name="addressLine1"
              control={control}
              render={({ field }) => (
                <Input
                  id="addressLine1"
                  type="text"
                  {...field}
                  className={errors.addressLine1 ? 'border-red-500' : ''}
                />
              )}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="addressLine2">Address Line 2</Label>
            <Controller
              name="addressLine2"
              control={control}
              render={({ field }) => <Input id="addressLine2" type="text" {...field} />}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="addressCity">City</Label>
              <Controller
                name="addressCity"
                control={control}
                render={({ field }) => (
                  <Input
                    id="addressCity"
                    type="text"
                    {...field}
                    className={errors.addressCity ? 'border-red-500' : ''}
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="addressState">State</Label>
              <Controller
                name="addressState"
                control={control}
                render={({ field }) => (
                  <Input
                    id="addressState"
                    type="text"
                    {...field}
                    className={errors.addressState ? 'border-red-500' : ''}
                  />
                )}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="addressPostCode">ZIP Code</Label>
            <Controller
              name="addressPostCode"
              control={control}
              render={({ field }) => (
                <Input
                  id="addressPostCode"
                  type="text"
                  {...field}
                  className={errors.addressPostCode ? 'border-red-500' : ''}
                />
              )}
            />
          </div>
        </div>
      </div>

      <div className="pt-4 border-t border-gray-200">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Skate Rental</h4>

        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Controller
              name="skateRentalActive"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="skateRentalActive"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              )}
            />
            <Label htmlFor="skateRentalActive" className="font-normal cursor-pointer">
              Needs rental skates
            </Label>
          </div>

          {skateRentalActive && (
            <>
              <div className="space-y-2">
                <Label htmlFor="skateRentalType">Skate Type</Label>
                <Controller
                  name="skateRentalType"
                  control={control}
                  rules={skateRentalActive ? { required: 'Skate type is required' } : {}}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger className={errors.skateRentalType ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Select skate type" />
                      </SelectTrigger>
                      <SelectContent>
                        {skateTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.skateRentalType && <p className="text-sm text-red-600">{errors.skateRentalType.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="skateRentalSize">Skate Size</Label>
                <Controller
                  name="skateRentalSize"
                  control={control}
                  rules={skateRentalActive ? { required: 'Skate size is required' } : {}}
                  render={({ field }) => (
                    <Input
                      id="skateRentalSize"
                      type="number"
                      step="0.5"
                      min="1"
                      max="20"
                      {...field}
                      className={errors.skateRentalSize ? 'border-red-500' : ''}
                    />
                  )}
                />
                {errors.skateRentalSize && <p className="text-sm text-red-600">{errors.skateRentalSize.message}</p>}
                <p className="text-sm text-gray-500">You can specify half sizes (e.g., 10.5)</p>
              </div>
            </>
          )}
        </div>
      </div>
    </form>
  )
}

StudentEditForm.propTypes = {
  student: PropTypes.object.isRequired,
  facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  onSubmitExposed: PropTypes.func,
  onSubmittingChange: PropTypes.func,
  allowLevelEditing: PropTypes.bool
}

export default StudentEditForm
